import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Package, ChevronDown, Menu, X, Truck, Boxes, Globe, Megaphone, Undo2 } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { DASHBOARD_SIGNUP_URL, TRACKING_URL } from '@/config/links';
import { RESOURCE_NAV_PATHS, ROUTES } from '@/config/routes';

const navLinkClass = 'text-gray-700 hover:text-blue-800 transition-colors font-medium';
const navLinkClassScrolled = 'text-blue-100 hover:text-white transition-colors font-medium';

const resourcesLinks = [
  { label: 'Blogs', href: ROUTES.BLOGS, type: 'link' },
  { label: 'Help Center', href: ROUTES.HELP_CENTER, type: 'link' },
  { label: 'FAQ', href: '/#faq', type: 'anchor' },
  { label: 'Contact Us', href: ROUTES.CONTACT, type: 'link' }
];

const productsSections = [
  {
    title: 'Shipping',
    key: 'shipping',
    items: [
      {
        label: 'Domestic Shipping',
        helper: 'Fast and reliable shipping across India.',
        href: '/products/domestic-shipping',
        icon: Truck,
        type: 'link'
      },
      {
        label: 'Boxd Cargo',
        helper: 'B2B bulk freight movement with flexible delivery options.',
        href: '/products/cargo-shipping',
        icon: Boxes,
        type: 'link'
      },
      {
        label: 'BoxdX',
        helper: 'International cross-border delivery with customs-ready support.',
        href: '/products/international-shipping',
        icon: Globe,
        type: 'link'
      }
    ]
  },
  {
    title: 'Growth & Marketing',
    key: 'growth',
    accent: 'emerald',
    items: [
      {
        label: 'Engage+',
        helper: 'Boost retention with post-purchase engagement journeys.',
        href: '/products/engage-plus',
        icon: Megaphone,
        type: 'link'
      },
      {
        label: 'RTO Predict',
        helper: 'Reduce return-to-origin rates with predictive intelligence.',
        href: '/products/rto-predict',
        icon: Undo2,
        type: 'link'
      }
    ]
  }
];

const ProductMenuItem = ({ item, accent, onClick }) => {
  const Icon = item.icon;
  const isEmerald = accent === 'emerald';

  const itemClasses = `group flex items-start gap-3 rounded-lg px-3 py-3 transition-all duration-200 ${isEmerald ? 'hover:bg-emerald-50' : 'hover:bg-blue-50'}`;
  const content = (
    <>
      <span className={`mt-0.5 rounded-md p-2 ${isEmerald ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-800'}`}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="block">
        <span className={`block text-sm font-semibold text-gray-800 ${isEmerald ? 'group-hover:text-emerald-800' : 'group-hover:text-blue-900'}`}>
          {item.label}
        </span>
        <span className="block text-xs text-gray-500">
          {item.helper}
        </span>
      </span>
    </>
  );

  if (item.type === 'link') {
    return (
      <Link to={item.href} onClick={onClick} className={itemClasses}>
        {content}
      </Link>
    );
  }

  return (
    <a href={item.href} onClick={onClick} className={itemClasses}>
      {content}
    </a>
  );
};

const DesktopProductsMegaMenu = ({ isOpen, onItemClick }) => (
  <div
    className={`absolute left-1/2 top-full w-[38rem] -translate-x-1/2 rounded-2xl border border-gray-200 bg-white p-5 shadow-xl transition-all duration-200 ${
      isOpen ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0'
    }`}
  >
    <div className="grid grid-cols-2 gap-6">
      {productsSections.map((section) => (
        <div key={section.key}>
          <h3 className="mb-3 text-base font-bold text-gray-900">{section.title}</h3>
          <div className="space-y-1">
            {section.items.map((item) => (
              <ProductMenuItem key={item.label} item={item} accent={section.accent} onClick={onItemClick} />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Navbar = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const [isMobileShippingOpen, setIsMobileShippingOpen] = useState(true);
  const [isMobileGrowthOpen, setIsMobileGrowthOpen] = useState(true);
  const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false);
  const closeDelayMs = 180;
  const productsRef = useRef(null);
  const resourcesRef = useRef(null);
  const productsCloseTimeoutRef = useRef(null);
  const resourcesCloseTimeoutRef = useRef(null);
  const isHome = location.pathname === ROUTES.HOME;
  const isFeaturesActive = isHome && location.hash === '#features';
  const isServicesActive = isHome && location.hash === '#services';
  const isPricingActive = location.pathname === ROUTES.PRICING;
  const isResourcesActive =
    RESOURCE_NAV_PATHS.includes(location.pathname) || (isHome && location.hash === '#faq');

  const getNavClass = (isActive) =>
    `${navLinkClass} ${isActive ? 'text-blue-800' : ''}`;

  const getDesktopNavClass = (isActive) =>
    isScrolled
      ? `${navLinkClassScrolled} ${isActive ? 'text-white' : ''}`
      : `${navLinkClass} ${isActive ? 'text-blue-800' : ''}`;

  const clearCloseTimeout = (timeoutRef) => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const scheduleProductsClose = () => {
    clearCloseTimeout(productsCloseTimeoutRef);
    productsCloseTimeoutRef.current = window.setTimeout(() => {
      setIsProductsOpen(false);
    }, closeDelayMs);
  };

  const scheduleResourcesClose = () => {
    clearCloseTimeout(resourcesCloseTimeoutRef);
    resourcesCloseTimeoutRef.current = window.setTimeout(() => {
      setIsResourcesOpen(false);
    }, closeDelayMs);
  };

  const handleProductsMouseEnter = () => {
    clearCloseTimeout(productsCloseTimeoutRef);
    setIsProductsOpen(true);
    setIsResourcesOpen(false);
  };

  const handleResourcesMouseEnter = () => {
    clearCloseTimeout(resourcesCloseTimeoutRef);
    setIsResourcesOpen(true);
    setIsProductsOpen(false);
  };

  const handleProductsBlur = (event) => {
    const nextFocused = event.relatedTarget;
    if (productsRef.current && nextFocused && productsRef.current.contains(nextFocused)) return;
    scheduleProductsClose();
  };

  const handleResourcesBlur = (event) => {
    const nextFocused = event.relatedTarget;
    if (resourcesRef.current && nextFocused && resourcesRef.current.contains(nextFocused)) return;
    scheduleResourcesClose();
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    const handleOutsideClick = (event) => {
      if (productsRef.current && !productsRef.current.contains(event.target)) {
        setIsProductsOpen(false);
      }
      if (resourcesRef.current && !resourcesRef.current.contains(event.target)) {
        setIsResourcesOpen(false);
      }
    };

    const handleEscapeClose = (event) => {
      if (event.key !== 'Escape') return;
      setIsProductsOpen(false);
      setIsResourcesOpen(false);
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscapeClose);
    return () => {
      clearCloseTimeout(productsCloseTimeoutRef);
      clearCloseTimeout(resourcesCloseTimeoutRef);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscapeClose);
    };
  }, []);

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      isScrolled ? 'bg-blue-950 shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center cursor-pointer group">
            <div className="rounded-lg bg-white p-1.5 shadow-sm transition-transform duration-300 group-hover:scale-105">
              <img
                src={`${process.env.PUBLIC_URL || ''}/logo.jpg`}
                alt="Boxd Logistics"
                className="h-11 w-auto object-contain"
              />
            </div>
          </Link>
          
          <div className="hidden md:flex items-center gap-6">
            <a href="/#features" className={getDesktopNavClass(isFeaturesActive)}>Features</a>
            <a href="/#services" className={getDesktopNavClass(isServicesActive)}>Services</a>
            <Link to="/pricing" className={getDesktopNavClass(isPricingActive)}>Pricing</Link>

            <div
              className="relative pb-2 -mb-2"
              ref={productsRef}
              onMouseEnter={handleProductsMouseEnter}
              onMouseLeave={scheduleProductsClose}
              onFocus={handleProductsMouseEnter}
              onBlur={handleProductsBlur}
            >
              <button
                type="button"
                aria-expanded={isProductsOpen}
                aria-haspopup="menu"
                onClick={() => {
                  clearCloseTimeout(productsCloseTimeoutRef);
                  setIsProductsOpen((prev) => !prev);
                  setIsResourcesOpen(false);
                }}
                className={`${getDesktopNavClass(false)} inline-flex items-center gap-1`}
              >
                Products
                <ChevronDown className={`h-4 w-4 transition-transform ${isProductsOpen ? 'rotate-180' : ''}`} />
              </button>
              <DesktopProductsMegaMenu
                isOpen={isProductsOpen}
                onItemClick={() => setIsProductsOpen(false)}
              />
            </div>

            <div
              className="relative pb-2 -mb-2"
              ref={resourcesRef}
              onMouseEnter={handleResourcesMouseEnter}
              onMouseLeave={scheduleResourcesClose}
              onFocus={handleResourcesMouseEnter}
              onBlur={handleResourcesBlur}
            >
              <button
                type="button"
                aria-expanded={isResourcesOpen}
                aria-haspopup="menu"
                onClick={() => {
                  clearCloseTimeout(resourcesCloseTimeoutRef);
                  setIsResourcesOpen((prev) => !prev);
                  setIsProductsOpen(false);
                }}
                className={`${getDesktopNavClass(isResourcesActive)} inline-flex items-center gap-1`}
              >
                Resources
                <ChevronDown className={`h-4 w-4 transition-transform ${isResourcesOpen ? 'rotate-180' : ''}`} />
              </button>
              {isResourcesOpen && (
                <div className="absolute right-0 top-full w-52 rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
                  {resourcesLinks.map((item) =>
                    item.type === 'link' ? (
                      <Link
                        key={item.label}
                        to={item.href}
                        className="block rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-800"
                        onClick={() => setIsResourcesOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <a
                        key={item.label}
                        href={item.href}
                        className="block rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-800"
                        onClick={() => setIsResourcesOpen(false)}
                      >
                        {item.label}
                      </a>
                    )
                  )}
                </div>
              )}
            </div>
            
            <Link to={TRACKING_URL}>
              <Button
                variant="outline"
                className={`font-semibold px-4 ${
                  isScrolled
                    ? 'border-2 border-blue-200 text-blue-50 hover:bg-blue-900'
                    : 'border-2 border-blue-800 text-blue-900 hover:bg-blue-50'
                }`}
              >
                <Package className="w-4 h-4 mr-2" />
                Track Order
              </Button>
            </Link>
            
            <Button 
              className="bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-950 text-white"
              onClick={() => { window.location.href = DASHBOARD_SIGNUP_URL; }}
            >
              Get Started
            </Button>
          </div>

          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 bg-white text-gray-700"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden pb-4">
            <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-lg space-y-2">
              <a href="/#features" className={`block px-3 py-2 rounded-lg hover:bg-blue-50 ${getNavClass(isFeaturesActive)}`}>Features</a>
              <a href="/#services" className={`block px-3 py-2 rounded-lg hover:bg-blue-50 ${getNavClass(isServicesActive)}`}>Services</a>
              <Link to="/pricing" className={`block px-3 py-2 rounded-lg hover:bg-blue-50 ${getNavClass(isPricingActive)}`}>Pricing</Link>

              <button
                type="button"
                onClick={() => setIsMobileProductsOpen((prev) => !prev)}
                className={`w-full px-3 py-2 rounded-lg hover:bg-blue-50 ${getNavClass(false)} inline-flex items-center justify-between`}
              >
                Products
                <ChevronDown className={`h-4 w-4 transition-transform ${isMobileProductsOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMobileProductsOpen && (
                <div className="pl-3 space-y-2">
                  {productsSections.map((section) => {
                    const isSectionOpen = section.key === 'shipping' ? isMobileShippingOpen : isMobileGrowthOpen;
                    const toggleSection = section.key === 'shipping' ? setIsMobileShippingOpen : setIsMobileGrowthOpen;

                    return (
                      <div key={section.key} className="rounded-lg border border-gray-200 bg-gray-50/40">
                        <button
                          type="button"
                          onClick={() => toggleSection((prev) => !prev)}
                          className="flex w-full items-center justify-between px-3 py-2 text-left"
                        >
                          <span className="text-sm font-semibold text-gray-900">{section.title}</span>
                          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isSectionOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isSectionOpen && (
                          <div className="space-y-1 px-2 pb-2">
                            {section.items.map((item) => (
                              <ProductMenuItem key={item.label} item={item} accent={section.accent} />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                onClick={() => setIsMobileResourcesOpen((prev) => !prev)}
                className={`w-full px-3 py-2 rounded-lg hover:bg-blue-50 ${getNavClass(isResourcesActive)} inline-flex items-center justify-between`}
              >
                Resources
                <ChevronDown className={`h-4 w-4 transition-transform ${isMobileResourcesOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMobileResourcesOpen && (
                <div className="pl-3 space-y-1">
                  {resourcesLinks.map((item) =>
                    item.type === 'link' ? (
                      <Link key={item.label} to={item.href} className="block px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-800">
                        {item.label}
                      </Link>
                    ) : (
                      <a key={item.label} href={item.href} className="block px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-800">
                        {item.label}
                      </a>
                    )
                  )}
                </div>
              )}

              <Link to={TRACKING_URL} className="block pt-2">
                <Button variant="outline" className="w-full border-2 border-blue-800 text-blue-900 hover:bg-blue-50 font-semibold">
                  <Package className="w-4 h-4 mr-2" />
                  Track Order
                </Button>
              </Link>
              <Button
                className="w-full bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-950 text-white"
                onClick={() => { window.location.href = DASHBOARD_SIGNUP_URL; }}
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;